import axios from "axios";
import { getErrorMessage } from "utils/errorHelpers";

/**
 * The wording this page uses for a stale `version` token — the concurrency conflict `accept` raises
 * (`DbUpdateConcurrencyExceptionFilter`, a 409) when the order changed after the copy on screen was
 * loaded. The backend's own message is written for any entity in the system; this one names what
 * the representative is looking at and what to do about it.
 */
export const staleOrderVersionMessage = "The quote has changed. Review it again.";

/**
 * True for the 409 a stale `version` token raises. Both `accept` and the order save carry a
 * version, so either can raise it.
 * @param error
 */
export function isStaleVersionConflict(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 409;
}

/**
 * True for the 404 an order route answers when nothing is recorded under the OD number — a
 * mistyped or a deleted one. The API's problem detail names the internal entity type and property
 * ("The entity 'Order' with OdNumber '...' could not be found."), which is right for an API and
 * wrong on a page, so a page checks this and writes its own sentence.
 * @param error
 */
export function isOrderNotFound(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}

/**
 * The wording this area uses for a refused order action. A 403 carries no body, so axios reports it
 * as "Request failed with status code 403", which tells a representative nothing they can act on.
 */
export const forbiddenOrderActionMessage = "You do not have permission to change this order.";

/**
 * True for the 403 an order route answers when the reader holds `Orders.Read` alone. Every write
 * endpoint on the area demands `Orders.ReadWrite`.
 * @param error
 */
export function isForbidden(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 403;
}

/**
 * The message to show for a failed order mutation. Two answers are reworded: the stale-version
 * conflict, which the backend states for any entity in the system, and the permission refusal,
 * which carries no message at all. Every other failure keeps the server's own words, which are the
 * useful ones.
 * @param error
 */
export function describeOrderError(error: unknown): string {
  if (isStaleVersionConflict(error)) {
    return staleOrderVersionMessage;
  }
  if (isForbidden(error)) {
    return forbiddenOrderActionMessage;
  }
  return getErrorMessage(error);
}
